import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { TranslationContext } from "../../shared/providers/translation/translation.provider";
import { IUnitProps } from "./Unit.interface";
import { Link, useNavigate, useParams } from "react-router-dom";
import { LoadingContext } from "../../shared/providers/loading/loading.provider";
import { useGetModule, useGetUnit } from "../../shared/hooks/useModules";
import { IModule, IModuleUnit } from "../../shared/interfaces/modules.interface";
import { routes } from "../../shared/router/routes";
import ComponentPicker from "../../components/UnitComponents/ComponentPicker/ComponentPicker";
import { ModuleContext } from "../../shared/providers/module/module.provider";
import { CONTENT_BASE_URL, DEFAULT_LANGUAGE } from "../../shared/constants";
import UnitCard from "../../components/UnitCard/UnitCard";
import Placeholder from "./../../shared/assets/imgs/placeholder.svg";
import { AnalyticsContext } from "../../shared/providers/analytics/analytics.provider";
import { UserContext } from "../../shared/providers/user/user.provider";
import ModuleCard from "../../components/ModuleCard/ModuleCard";

function Unit(props: IUnitProps) {
    const navigate = useNavigate();
    const { lang, translate } = useContext(TranslationContext);
    const { setIsLoading } = useContext(LoadingContext);
    const { modules, module, setModule, setCurrentUnitId, setUpdated } = useContext(ModuleContext);
    const { user } = useContext(UserContext);
    const { setEvent } = useContext(AnalyticsContext);

    const { unitId: id, moduleId } = useParams();
    const { isLoading, data: unit } = useGetUnit<IModuleUnit>(id, user);
    const { data: moduleData } = useGetModule<IModule>(moduleId, lang ?? DEFAULT_LANGUAGE, user);
    const [nextModule, setNextModule] = useState<IModule | undefined>();

    useEffect(() => {
        window.scrollTo({ top: 0 });
    }, []);

    const nextIndex = useMemo(
        () => (module?.units?.findIndex((unit) => unit.id === id) ?? 0) + 1,
        [module, id]
    );
    const hasNextUnit = useMemo(() => module && module.units?.[nextIndex], [nextIndex, module]);

    useEffect(() => {
        if (!modules || !module) return;

        const currentModuleIndex = modules.findIndex((mod) => mod.id === module.id);
        const nextModule = modules[currentModuleIndex + 1];

        if (nextModule && (!nextModule.moduleProgress || nextModule.moduleProgress < 100)) {
            // HAS A NEXT UNCOMPLETED MODULE
            setNextModule(nextModule);
        }
        else {
            // FINDS THE NEXT UNCOMPLETED MODULE
            const uncompletedModule = modules.find(mod => !mod.moduleProgress || mod.moduleProgress < 100);
            setNextModule(uncompletedModule);
        }
    }, [module, modules]);

    useEffect(() => {
        if (!user) return;

        setEvent({
            event_name: "app_module_unit",
            event_category: "content_navigation",
            event_action: "app_module_unit",
            user_token: user?.id,
            company_id: user?.company.id,
            module_id: moduleId,
            unit_id: id
        });

    }, [user]);

    useEffect(() => {
        setIsLoading(isLoading || !module);

        if (unit || isLoading) return;

        if (isLoading) return;
        if (!unit) navigate(routes.MODULES.path);
    }, [isLoading, module, navigate, unit]);

    useEffect(() => {
        if (module) return;

        setModule(moduleData);
    }, [module, moduleData]);

    useEffect(() => {
        setCurrentUnitId(id);
    }, [id]);

    useEffect(() => {
        return () => setUpdated(new Date());
    }, []);

    return (
        <div className="unit d-flex flex-column h-100 bg-neutrals-white">
            <div className="unit_nav p-1">
                <Link
                    to={".."}
                    className=" d-flex align-items-center gap-01 color-neutrals-black text-bd2-rg">
                    <i className="icon icon-16 icon-Chevron--left"></i>
                    {translate("modules.table_of_contents", "Table of contents")}
                </Link>
            </div>
            <div className="d-flex flex-column h-100 gap-1 pb-2">
                <div className="container-fluid gx-2 pt-2">
                    <div className="row gx-0">
                        <div className="col-12 d-flex flex-column gap-02">
                            <span className="text-bd3-rg color-neutrals-dark">
                                Unit {nextIndex}
                            </span>
                            <h1 className="text-h2-smbd color-neutrals-black mb-02">
                                {unit?.title}
                            </h1>
                            <p className="text-bd2-rg color-neutrals-black m-0">
                                {unit?.description}
                            </p>
                        </div>
                    </div>
                </div>
                {unit?.items.map((item, i) => (
                    <ComponentPicker key={i} unitId={id} {...item}></ComponentPicker>
                ))}
            </div>
            {hasNextUnit && (
                <div className="container-fluid gx-2 bg-blue-50 pt-2 pb-6">
                    <div className="row gx-0">
                        <div className="col-12">
                            <h2 className="text-h4-smbd mb-12">
                                {translate("modules.next_module_unit", "Next module unit")}
                            </h2>
                            <UnitCard
                                moduleId={module!.id}
                                moduleImage={
                                    module!.image?.url
                                        ? `${CONTENT_BASE_URL}${module!.image?.url}`
                                        : Placeholder
                                }
                                unit={module!.units?.[nextIndex] as IModuleUnit}
                                index={nextIndex + 1}
                                fromUnit
                            />
                        </div>
                    </div>
                </div>
            )}
            {nextModule && !hasNextUnit && (
                <div className="container-fluid gx-2 bg-blue-50 pt-2 pb-6">
                    <div className="row gx-0">
                        <div className="col-12">
                            <div className="d-flex flex-column gap-12">
                                <h2 className="text-h4-smbd">
                                    {translate("modules.start_new_module", "Start a new module")}
                                </h2>
                                <ModuleCard {...nextModule} fromUnit />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Unit;
